import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import api from './api';

const AuthContext = createContext();

const TOKEN_KEY  = 'admin_token';
const USER_KEY   = 'admin_user';
const TOKEN_TTL_MS = 18 * 60 * 60 * 1000; // 18 hours
// Refresh the token when less than 30 min remain
const REFRESH_THRESHOLD_MS = 30 * 60 * 1000;

function getTokenExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch { return null; }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(() => JSON.parse(localStorage.getItem(USER_KEY) || 'null'));
  const [loading, setLoading] = useState(true);
  const refreshTimer          = useRef(null);

  const doLogout = useCallback(() => {
    api.post('/auth/logout').catch(() => {});
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
  }, []);

  const scheduleRefresh = useCallback((token) => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    const expiry = getTokenExpiry(token);
    if (!expiry) return;
    const now = Date.now();
    const msUntilRefresh = (expiry - now) - REFRESH_THRESHOLD_MS;
    const msUntilExpiry  = expiry - now;

    if (msUntilExpiry <= 0) { doLogout(); return; }

    const delay = msUntilRefresh > 0 ? msUntilRefresh : 0;
    refreshTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.post('/auth/refresh');
        localStorage.setItem(TOKEN_KEY, data.token);
        scheduleRefresh(data.token);
      } catch {
        doLogout();
      }
    }, delay);
  }, [doLogout]);

  // Helper: store token + fetch user
  const loginWithToken = useCallback(async (token) => {
    localStorage.setItem(TOKEN_KEY, token);
    const r = await api.get('/auth/me');
    localStorage.setItem(USER_KEY, JSON.stringify(r.data));
    setUser(r.data);
    scheduleRefresh(token);
  }, [scheduleRefresh]);

  // On mount: handle SSO token from URL, then verify stored token
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get('sso_token');
    if (ssoToken) {
      // Remove token from URL without reload
      const clean = window.location.pathname;
      window.history.replaceState({}, '', clean);
      loginWithToken(ssoToken).catch(() => {}).finally(() => setLoading(false));
      return;
    }
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      const expiry = getTokenExpiry(token);
      if (expiry && Date.now() >= expiry) {
        // Already expired - clear immediately
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setUser(null);
        setLoading(false);
        return;
      }
      api.get('/auth/me')
        .then(r => {
          setUser(r.data);
          scheduleRefresh(token);
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
          setUser(null);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
    return () => { if (refreshTimer.current) clearTimeout(refreshTimer.current); };
  }, [scheduleRefresh]);

  // Step 1: username + password
  const loginStep1 = async (username, password) => {
    const { data } = await api.post('/auth/login', { username, password });
    // data: { mfaRequired, partialToken } OR { mfaSetupRequired, token, user }
    if (data.mfaRequired) {
      return { mfaRequired: true, partialToken: data.partialToken };
    }
    // MFA not enrolled yet - store token, force setup
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setUser({ ...data.user, mfaSetupRequired: true });
    scheduleRefresh(data.token);
    return { mfaSetupRequired: true };
  };

  // Step 2: TOTP code
  const loginStep2 = async (partialToken, code) => {
    const { data } = await api.post('/auth/mfa/verify', { partialToken, code });
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setUser(data.user);
    scheduleRefresh(data.token);
  };

  // After MFA setup confirmed - replace token
  const afterMfaSetup = (newToken) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    setUser(u => ({ ...u, mfaSetupRequired: false, mfa_enabled: 1 }));
    scheduleRefresh(newToken);
  };

  const logout = doLogout;

  const hasRole = (minRole) => {
    const levels = { readonly: 1, operator: 2, admin: 3, super_admin: 4 };
    return (levels[user?.role] || 0) >= (levels[minRole] || 99);
  };

  return (
    <AuthContext.Provider value={{ user, loginStep1, loginStep2, afterMfaSetup, logout, loading, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
